(() => {
  const FACET_TOGGLE_SELECTOR = '.ss__facet__header, .cprp-filter-group__toggle';
  const SHOW_MORE_SELECTOR = 'a.ss__show-more__link';
  const VARIANT_MORE_SELECTOR = 'a.ss__variant--more';
  const VARIANT_SWATCH_SELECTOR = 'button.ss__variant:not(.ss__variant--more), a.ss__variant:not(.ss__variant--more), button[data-swatch]';
  const SWATCH_LIVE_REGION_ID = 'variant-swatch-live-region';

  function getFacetToggleTitle(btn) {
    const ssTitle = btn.querySelector('.ss__title');
    if (ssTitle) return ssTitle.textContent.trim();

    const clone = btn.cloneNode(true);
    clone.querySelectorAll('[aria-hidden="true"], .cprp-icon-chevron').forEach((el) => el.remove());
    return clone.textContent.replace(/\s+/g, ' ').trim();
  }

  function setFacetToggleLabel(btn) {
    if (!btn.matches(FACET_TOGGLE_SELECTOR)) return;

    const title = getFacetToggleTitle(btn);
    if (!title) return;

    const expanded = btn.getAttribute('aria-expanded') === 'true';
    btn.setAttribute('aria-label', `${title} filter, ${expanded ? 'expanded' : 'collapsed'}`);
  }

  function getShowMoreLabel(link) {
    const visibleText = link.textContent.replace(/\s+/g, ' ').trim();
    const normalized = visibleText.toLowerCase();

    if (normalized === 'show more') return 'Show more filter options';
    if (normalized === 'show less') return 'Show fewer filter options';
    return visibleText;
  }

  function bindShowMoreKeyboard(link) {
    if (link.dataset.a11yKeyboardBound === 'true') return;

    link.addEventListener('keydown', (event) => {
      if (event.key !== ' ' && event.key !== 'Spacebar') return;
      event.preventDefault();
      link.click();
    });

    link.dataset.a11yKeyboardBound = 'true';
  }

  function enhanceShowMoreLink(link) {
    if (!link.matches(SHOW_MORE_SELECTOR)) return;

    const href = link.getAttribute('href');
    if (href && href.trim()) return;

    link.setAttribute('role', 'button');

    const label = getShowMoreLabel(link);
    if (label) {
      link.setAttribute('aria-label', label);
    }

    bindShowMoreKeyboard(link);
  }

  function getVariantMoreLabel(link) {
    const countMatch = link.textContent.match(/\+(\d+)/);
    const count = countMatch ? countMatch[1] : '';

    const card = link.closest('.ss__result, .product-item, .cprp-card');
    const titleEl = card?.querySelector('.product-item__title, .cprp-card__title');
    const productName = titleEl?.textContent.replace(/\s+/g, ' ').trim();

    if (productName && count) {
      return `${count} more colors for ${productName}`;
    }
    if (count) return `View ${count} more colors`;
    return 'View more color options';
  }

  function enhanceVariantMoreLink(link) {
    if (!link.matches(VARIANT_MORE_SELECTOR)) return;

    const label = getVariantMoreLabel(link);
    if (label) {
      link.setAttribute('aria-label', label);
    }
  }

  function titleCaseColor(value) {
    return value
      .trim()
      .replace(/[-_]+/g, ' ')
      .split(/\s+/)
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  }

  function getVariantSwatchColorName(swatch) {
    const dataColor = swatch.getAttribute('data-color');
    if (dataColor) return titleCaseColor(dataColor);

    const ariaLabel = (swatch.getAttribute('aria-label') || '').trim();
    if (ariaLabel && !/^view\s/i.test(ariaLabel)) return titleCaseColor(ariaLabel);

    const title = (swatch.getAttribute('title') || '').trim();
    if (title) return titleCaseColor(title);

    return '';
  }

  function getVariantSwatchLabel(swatch) {
    const color = getVariantSwatchColorName(swatch);
    if (color) return `View ${color} color`;
    return 'View color option';
  }

  function setElementText(el, text) {
    if (!el || el.textContent === text) return;
    el.textContent = text;
  }

  function ensureSrOnlyLabel(swatch, label) {
    let labelEl = swatch.querySelector('[data-variant-swatch-sr-label]');

    if (!labelEl) {
      labelEl = document.createElement('span');
      labelEl.className = 'sr-only';
      labelEl.setAttribute('data-variant-swatch-sr-label', '');
      swatch.appendChild(labelEl);
    }

    if (!labelEl.id) {
      labelEl.id = `variant-swatch-label-${Math.random().toString(36).slice(2, 10)}`;
    }

    setElementText(labelEl, label);
    swatch.setAttribute('aria-labelledby', labelEl.id);
  }

  function getSwatchLiveRegion() {
    let liveRegion = document.getElementById(SWATCH_LIVE_REGION_ID);

    if (!liveRegion) {
      liveRegion = document.createElement('div');
      liveRegion.id = SWATCH_LIVE_REGION_ID;
      liveRegion.className = 'sr-only';
      liveRegion.setAttribute('role', 'status');
      liveRegion.setAttribute('aria-live', 'polite');
      liveRegion.setAttribute('aria-atomic', 'true');
      document.body.appendChild(liveRegion);
    }

    return liveRegion;
  }

  function announceVariantSwatch(swatch) {
    if (!swatch?.matches?.(VARIANT_SWATCH_SELECTOR)) return;

    const label = getVariantSwatchLabel(swatch);
    const liveRegion = getSwatchLiveRegion();

    liveRegion.textContent = '';
    window.setTimeout(() => {
      liveRegion.textContent = label;
    }, 20);
  }

  function enhanceVariantSwatch(swatch) {
    if (!swatch.matches(VARIANT_SWATCH_SELECTOR)) return;

    const label = getVariantSwatchLabel(swatch);
    swatch.setAttribute('aria-label', label);
    swatch.setAttribute('title', label);
    ensureSrOnlyLabel(swatch, label);
  }

  function bindVariantSwatchHoverAnnouncements(section) {
    if (section.dataset.variantSwatchHoverBound === 'true') return;

    section.addEventListener('mouseover', (event) => {
      const swatch = event.target.closest(VARIANT_SWATCH_SELECTOR);
      if (!swatch || swatch.contains(event.relatedTarget)) return;
      announceVariantSwatch(swatch);
    });

    section.addEventListener('focusin', (event) => {
      const swatch = event.target.closest(VARIANT_SWATCH_SELECTOR);
      if (!swatch) return;
      announceVariantSwatch(swatch);
    });

    section.dataset.variantSwatchHoverBound = 'true';
  }

  function initFacetToggleAccessibility(section) {
    const enhanceSearchspringA11y = (root = section) => {
      root.querySelectorAll(FACET_TOGGLE_SELECTOR).forEach(setFacetToggleLabel);
      root.querySelectorAll(SHOW_MORE_SELECTOR).forEach(enhanceShowMoreLink);
      root.querySelectorAll(VARIANT_MORE_SELECTOR).forEach(enhanceVariantMoreLink);
      root.querySelectorAll(VARIANT_SWATCH_SELECTOR).forEach(enhanceVariantSwatch);
    };

    enhanceSearchspringA11y();

    const observer = new MutationObserver((mutations) => {
      let shouldEnhance = false;

      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'aria-expanded') {
          if (mutation.target.matches(FACET_TOGGLE_SELECTOR)) {
            setFacetToggleLabel(mutation.target);
          }
          return;
        }

        if (mutation.type === 'childList') {
          shouldEnhance = true;
        }
      });

      if (shouldEnhance) {
        enhanceSearchspringA11y();
      }
    });

    observer.observe(section, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['aria-expanded'],
    });

    section.addEventListener('click', (event) => {
      const showMoreLink = event.target.closest(SHOW_MORE_SELECTOR);
      if (showMoreLink) {
        requestAnimationFrame(() => enhanceShowMoreLink(showMoreLink));
        return;
      }

      const btn = event.target.closest(FACET_TOGGLE_SELECTOR);
      if (!btn) return;
      requestAnimationFrame(() => setFacetToggleLabel(btn));
    });

    bindVariantSwatchHoverAnnouncements(section);
  }

  const MOBILE_MAX = 1023;
  const FILTER_OFFSET = 16;
  const DESKTOP_FILTER_TOP = 18;
  const STICKY_HYSTERESIS = 4;
  const FILTER_ENTER_DURATION = 380;
  const PAGE_TOP_THRESHOLD = 8;
  const HEADER_REVEAL_MS = 300;

  const syncedFilterTops = new WeakMap();
  const sectionCleanups = new WeakMap();
  const customScrollbarState = new WeakMap();
  const anchorSubscribers = new Set();
  let mobileHeaderAnchorReady = false;
  let headerRevealUntil = 0;
  let wasNearPageTop = false;

  const mobileHeaderAnchor = {
    filterTop: FILTER_OFFSET,
  };

  function prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function isHeaderRevealing() {
    return performance.now() < headerRevealUntil;
  }

  function markHeaderReveal() {
    headerRevealUntil = performance.now() + HEADER_REVEAL_MS;
  }

  function isNearPageTop() {
    return (window.scrollY || document.documentElement.scrollTop) <= PAGE_TOP_THRESHOLD;
  }

  function checkPageTopAnchorRefresh() {
    const nearTop = isNearPageTop();
    if (nearTop && !wasNearPageTop) {
      refreshMobileHeaderAnchor(true);
    }
    wasNearPageTop = nearTop;
  }

  function shouldReleaseFixedToolbar(sentinel, filterTop) {
    if (isNearPageTop()) return true;

    const sentinelBottom = sentinel.getBoundingClientRect().bottom;
    return sentinelBottom > filterTop + STICKY_HYSTERESIS;
  }

  function getMainHeaderStickyTop() {
    return window.innerWidth <= 767 ? 30 : 36;
  }

  function getStickyStackBottom() {
    const utility = document.querySelector('.utility-banner-part');
    const main = document.querySelector('.main-header-part');
    if (!utility && !main) return 0;

    const utilityUp = utility?.classList.contains('scrollup');
    const mainUp = main?.classList.contains('scrollup');
    const mainStickyTop = getMainHeaderStickyTop();

    if (utilityUp && mainUp && main) {
      return mainStickyTop + main.offsetHeight;
    }
    if (utilityUp && utility) {
      return utility.offsetHeight;
    }
    if (mainUp && main) {
      return main.offsetHeight;
    }
    return 0;
  }

  function getFlowHeaderBottom() {
    const utility = document.querySelector('.utility-banner-part');
    const main = document.querySelector('.main-header-part');
    let total = 0;

    if (utility?.offsetHeight) total += utility.offsetHeight;
    if (main?.offsetHeight) total += main.offsetHeight;

    return total;
  }

  function refreshMobileHeaderAnchor(snap = false) {
    let nextTop = FILTER_OFFSET;

    if (window.innerWidth <= MOBILE_MAX) {
      const stackBottom = getStickyStackBottom();
      if (stackBottom > 0) {
        nextTop = stackBottom + FILTER_OFFSET;
      } else {
        const scrollY = window.scrollY || document.documentElement.scrollTop;
        if (scrollY <= 4) {
          const flowBottom = getFlowHeaderBottom();
          nextTop = flowBottom > 0 ? flowBottom + FILTER_OFFSET : FILTER_OFFSET;
        }
      }
    }

    if (mobileHeaderAnchor.filterTop === nextTop && !snap) return;

    const prevTop = mobileHeaderAnchor.filterTop;
    mobileHeaderAnchor.filterTop = nextTop;

    if (!snap && !prefersReducedMotion() && prevTop !== nextTop) {
      markHeaderReveal();
      emitMobileHeaderAnchorChange({ animateFrom: prevTop, animateTo: nextTop });
      return;
    }

    if (nextTop > prevTop) {
      markHeaderReveal();
    }

    emitMobileHeaderAnchorChange({ snap: true });
  }

  function registerSectionCleanup(section, cleanup) {
    if (!sectionCleanups.has(section)) {
      sectionCleanups.set(section, []);
    }
    sectionCleanups.get(section).push(cleanup);
  }

  function destroySection(section) {
    const cleanups = sectionCleanups.get(section);
    if (cleanups) {
      cleanups.forEach((cleanup) => {
        try {
          cleanup();
        } catch (error) {
          // Section DOM may already be detached during theme editor unload.
        }
      });
      sectionCleanups.delete(section);
    }

    syncedFilterTops.delete(section);
    delete section.dataset.cprpInit;
    delete section.dataset.cprpColWidthLocked;
  }

  function getSectionFromEvent(event) {
    return (
      event.target.querySelector?.('.collection-page-redesign-prototype')
      || (event.target.classList?.contains('collection-page-redesign-prototype') ? event.target : null)
    );
  }

  function subscribeMobileHeaderAnchor(callback) {
    anchorSubscribers.add(callback);
    return () => anchorSubscribers.delete(callback);
  }

  function emitMobileHeaderAnchorChange(detail = {}) {
    anchorSubscribers.forEach((callback) => callback(detail));
  }

  function bindHeaderAnchorSync() {
    let refreshRaf = 0;

    const scheduleRefresh = () => {
      if (refreshRaf) return;
      refreshRaf = requestAnimationFrame(() => {
        refreshRaf = 0;
        refreshMobileHeaderAnchor(false);
      });
    };

    document.querySelectorAll('.utility-banner-part, .main-header-part').forEach((el) => {
      const observer = new MutationObserver(scheduleRefresh);
      observer.observe(el, { attributes: true, attributeFilter: ['class'] });
    });
  }

  function ensureMobileHeaderAnchor() {
    if (mobileHeaderAnchorReady) return;
    mobileHeaderAnchorReady = true;
    refreshMobileHeaderAnchor(true);
    bindHeaderAnchorSync();
    window.addEventListener(
      'resize',
      () => {
        if (window.innerWidth > MOBILE_MAX) return;
        refreshMobileHeaderAnchor(true);
      },
      { passive: true }
    );
  }

  function getMobileFilterTop() {
    return mobileHeaderAnchor.filterTop;
  }

  function syncMobileFilterTop(section, top, snap = false) {
    const rounded = Math.round(typeof top === 'number' ? top : getMobileFilterTop());
    if (syncedFilterTops.get(section) === rounded) return;
    syncedFilterTops.set(section, rounded);
    section.style.setProperty('--cprp-mobile-filter-top', `${rounded}px`);

    if (snap) {
      section.classList.add('is-anchor-snap');
      requestAnimationFrame(() => {
        section.classList.remove('is-anchor-snap');
      });
    }
  }

  function animateHeaderReveal(section, el, fromTop, toTop) {
    if (prefersReducedMotion() || Math.abs(fromTop - toTop) < 1) {
      syncMobileFilterTop(section, toTop, true);
      return;
    }

    markHeaderReveal();
    el.classList.add('is-header-reveal');
    syncMobileFilterTop(section, fromTop, true);

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        syncMobileFilterTop(section, toTop);

        let finished = false;
        const finish = () => {
          if (finished) return;
          finished = true;
          el.classList.remove('is-header-reveal');
        };

        el.addEventListener('transitionend', finish, { once: true });
        window.setTimeout(finish, HEADER_REVEAL_MS + 50);
      });
    });
  }

  function playFixedEnter(toolbar, offsetY) {
    toolbar.classList.remove('is-entering');
    toolbar.style.removeProperty('--cprp-toolbar-offset-y');

    if (prefersReducedMotion() || Math.abs(offsetY) < 1) return;

    toolbar.style.setProperty('--cprp-toolbar-offset-y', `${offsetY}px`);
    toolbar.classList.add('is-entering');

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        toolbar.style.setProperty('--cprp-toolbar-offset-y', '0px');

        let finished = false;
        const finish = () => {
          if (finished) return;
          finished = true;
          toolbar.classList.remove('is-entering');
          toolbar.style.removeProperty('--cprp-toolbar-offset-y');
        };

        toolbar.addEventListener('transitionend', finish, { once: true });
        window.setTimeout(finish, FILTER_ENTER_DURATION + 80);
      });
    });
  }

  function getVisibleHeaderBottom() {
    let bottom = 0;

    document.querySelectorAll('.utility-banner-part, .main-header-part').forEach((el) => {
      const rect = el.getBoundingClientRect();
      const isStuck = el.classList.contains('scrollup');
      if (rect.bottom <= 0) return;
      if (isStuck || rect.top >= 0) {
        bottom = Math.max(bottom, rect.bottom);
      }
    });

    if (bottom === 0) {
      const header = document.getElementById('header');
      const desktopHeader = header?.querySelector('[data-site-header-inner]');
      const mobileHeader = header?.querySelector('.lg\\:hidden');
      const fallbackHeader = window.innerWidth >= 1024 ? desktopHeader : mobileHeader;
      if (fallbackHeader) {
        const rect = fallbackHeader.getBoundingClientRect();
        if (rect.bottom > 0 && rect.top >= 0) {
          bottom = Math.max(bottom, rect.bottom);
        }
      }
    }

    return bottom;
  }

  function getDesktopFilterTop() {
    const headerBottom = getVisibleHeaderBottom();
    return headerBottom > 0 ? headerBottom + DESKTOP_FILTER_TOP : DESKTOP_FILTER_TOP;
  }

  function clearDesktopFilterMetrics(section) {
    [
      '--cprp-filter-col-width',
      '--cprp-filter-inner-width',
      '--cprp-filter-pad-right',
      '--cprp-filter-pad-left',
    ].forEach((name) => section.style.removeProperty(name));
    delete section.dataset.cprpColWidthLocked;
  }

  function lockDesktopFilterColumnWidth(section, filters, force = false) {
    if (section.dataset.cprpColWidthLocked && !force) return;

    const style = getComputedStyle(filters);
    const padLeft = parseFloat(style.paddingLeft) || 0;
    const padRight = parseFloat(style.paddingRight) || 0;
    const colWidth = filters.clientWidth;
    const innerWidth = Math.max(0, colWidth - padLeft - padRight);

    if (colWidth <= 0 || innerWidth <= 0) return;

    section.style.setProperty('--cprp-filter-col-width', `${colWidth}px`);
    section.style.setProperty('--cprp-filter-inner-width', `${innerWidth}px`);
    section.style.setProperty('--cprp-filter-pad-right', `${padRight}px`);
    section.style.setProperty('--cprp-filter-pad-left', `${padLeft}px`);
    section.dataset.cprpColWidthLocked = '1';
  }

  function updateFilterScrollMax(section, sticky) {
    const top = getDesktopFilterTop();
    const stickyTop = sticky.getBoundingClientRect().top;
    const maxScroll = Math.max(200, window.innerHeight - Math.max(top, stickyTop));
    section.style.setProperty('--cprp-filter-scroll-max', `${maxScroll}px`);
  }

  function updateFilterScrollOverflow(scrollOuter) {
    const state = customScrollbarState.get(scrollOuter);
    if (state) {
      state.updateMetrics();
      return;
    }

    if (!scrollOuter) return;
    const hasScroll = scrollOuter.scrollHeight > scrollOuter.clientHeight + 1;
    scrollOuter.classList.toggle('has-scroll', hasScroll);
  }

  function initCustomFilterScrollbar(scrollOuter) {
    if (!scrollOuter || customScrollbarState.has(scrollOuter)) {
      return customScrollbarState.get(scrollOuter);
    }

    const trackParent = scrollOuter.parentElement;
    if (!trackParent) return null;

    let track = Array.from(trackParent.children).find((el) =>
      el.classList?.contains('cprp-filters__scrollbar')
    );
    if (!track) {
      track = scrollOuter.querySelector('.cprp-filters__scrollbar');
    }
    let thumb = track?.querySelector('.cprp-filters__scrollbar-thumb');

    if (!track) {
      track = document.createElement('div');
      track.className = 'cprp-filters__scrollbar';
      track.setAttribute('aria-hidden', 'true');
      thumb = document.createElement('div');
      thumb.className = 'cprp-filters__scrollbar-thumb';
      track.appendChild(thumb);
    }
    trackParent.appendChild(track);

    scrollOuter.classList.add('cprp-filters__scroll-outer--custom-scrollbar');

    const THUMB_MIN_HEIGHT = 40;

    const updateMetrics = () => {
      const section = scrollOuter.closest('.collection-page-redesign-prototype');

      if (
        !scrollOuter.isConnected ||
        window.innerWidth < 1024 ||
        section?.classList.contains('cprp-filters-hidden')
      ) {
        track.hidden = true;
        scrollOuter.classList.remove('has-scroll');
        return;
      }

      const { scrollHeight, clientHeight, scrollTop } = scrollOuter;
      const hasScroll = scrollHeight > clientHeight + 1;
      scrollOuter.classList.toggle('has-scroll', hasScroll);
      track.hidden = !hasScroll;

      if (!hasScroll) {
        thumb.style.height = '';
        thumb.style.transform = '';
        return;
      }

      const trackHeight = clientHeight;
      const thumbHeight = Math.max(
        THUMB_MIN_HEIGHT,
        Math.round((clientHeight / scrollHeight) * trackHeight)
      );
      const maxThumbTop = Math.max(0, trackHeight - thumbHeight);
      const scrollable = scrollHeight - clientHeight;
      const thumbTop = scrollable > 0 ? Math.round((scrollTop / scrollable) * maxThumbTop) : 0;

      thumb.style.height = `${thumbHeight}px`;
      thumb.style.transform = `translate3d(0, ${thumbTop}px, 0)`;
    };

    const onScroll = () => updateMetrics();
    scrollOuter.addEventListener('scroll', onScroll, { passive: true });

    const onThumbPointerDown = (e) => {
      if (e.button !== 0) return;
      e.preventDefault();

      const startY = e.clientY;
      const startScrollTop = scrollOuter.scrollTop;
      const scrollRange = scrollOuter.scrollHeight - scrollOuter.clientHeight;
      const thumbRange = track.clientHeight - thumb.offsetHeight;

      const onMove = (moveEvent) => {
        if (thumbRange <= 0 || scrollRange <= 0) return;
        const deltaY = moveEvent.clientY - startY;
        scrollOuter.scrollTop = startScrollTop + (deltaY / thumbRange) * scrollRange;
      };

      const onUp = () => {
        document.removeEventListener('pointermove', onMove);
        document.removeEventListener('pointerup', onUp);
        document.removeEventListener('pointercancel', onUp);
      };

      document.addEventListener('pointermove', onMove);
      document.addEventListener('pointerup', onUp);
      document.addEventListener('pointercancel', onUp);
    };

    const onTrackPointerDown = (e) => {
      if (e.button !== 0 || e.target === thumb) return;

      const rect = track.getBoundingClientRect();
      const scrollRange = scrollOuter.scrollHeight - scrollOuter.clientHeight;
      const thumbRange = track.clientHeight - thumb.offsetHeight;
      if (scrollRange <= 0 || thumbRange <= 0) return;

      const clickOffset = e.clientY - rect.top - thumb.offsetHeight / 2;
      const ratio = Math.min(1, Math.max(0, clickOffset / thumbRange));
      scrollOuter.scrollTop = ratio * scrollRange;
    };

    thumb.addEventListener('pointerdown', onThumbPointerDown);
    track.addEventListener('pointerdown', onTrackPointerDown);

    const cleanup = () => {
      scrollOuter.removeEventListener('scroll', onScroll);
      thumb.removeEventListener('pointerdown', onThumbPointerDown);
      track.removeEventListener('pointerdown', onTrackPointerDown);
      track.remove();
      scrollOuter.classList.remove('cprp-filters__scroll-outer--custom-scrollbar', 'has-scroll');
      customScrollbarState.delete(scrollOuter);
    };

    const state = { updateMetrics, cleanup };
    customScrollbarState.set(scrollOuter, state);
    updateMetrics();
    return state;
  }

  function initDesktopFilterSticky(section) {
    const filters = section.querySelector('.cprp-filters');
    const sticky = section.querySelector('.cprp-filters__sticky');
    const scrollOuter = sticky?.querySelector('.cprp-filters__scroll-outer');
    const scrollInner = scrollOuter?.querySelector('.cprp-filters__scroll-inner');
    if (!filters || !sticky) return;

    const customScrollbar = scrollOuter ? initCustomFilterScrollbar(scrollOuter) : null;

    let ticking = false;

    const update = () => {
      ticking = false;

      if (window.innerWidth < 1024 || section.classList.contains('cprp-filters-hidden')) {
        clearDesktopFilterMetrics(section);
        section.style.removeProperty('--cprp-filter-scroll-max');
        return;
      }

      lockDesktopFilterColumnWidth(section, filters);
      section.style.setProperty('--cprp-filter-sticky-top', `${getDesktopFilterTop()}px`);
      updateFilterScrollMax(section, sticky);
      updateFilterScrollOverflow(scrollOuter);
    };

    const requestUpdate = () => {
      if (!ticking) {
        ticking = true;
        requestAnimationFrame(update);
      }
    };

    const onResize = () => {
      if (window.innerWidth >= 1024 && !section.classList.contains('cprp-filters-hidden')) {
        lockDesktopFilterColumnWidth(section, filters, true);
      }
      requestUpdate();
    };

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (window.innerWidth >= 1024 && !section.classList.contains('cprp-filters-hidden')) {
          lockDesktopFilterColumnWidth(section, filters, true);
        }
        requestUpdate();
      });
    });

    window.addEventListener('scroll', requestUpdate, { passive: true });
    window.addEventListener('resize', onResize, { passive: true });

    let resizeObserver = null;
    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(requestUpdate);
      resizeObserver.observe(filters);
      if (scrollOuter) resizeObserver.observe(scrollOuter);
      if (scrollInner) resizeObserver.observe(scrollInner);
    }

    registerSectionCleanup(section, () => {
      window.removeEventListener('scroll', requestUpdate);
      window.removeEventListener('resize', onResize);
      resizeObserver?.disconnect();
      customScrollbar?.cleanup();
      clearDesktopFilterMetrics(section);
      section.style.removeProperty('--cprp-filter-scroll-max');
    });
  }

  function initAthosToolbarSticky(section) {
    const wrap = section.querySelector('[data-cprp-athos-toolbar-wrap]');
    const toolbar = section.querySelector('#athos-toolbar');
    const sentinel = section.querySelector('[data-cprp-athos-toolbar-sentinel]');
    if (!wrap || !toolbar || !sentinel) return;

    ensureMobileHeaderAnchor();

    let wasFixed = false;
    let ticking = false;

    const isStickyEnabled = () => window.innerWidth <= MOBILE_MAX;

    const isToolbarReady = () =>
      Boolean(toolbar.querySelector('.ss__toolbar') || toolbar.children.length > 0);

    const clearFixed = () => {
      toolbar.classList.remove('is-fixed', 'is-entering', 'is-header-reveal');
      toolbar.style.removeProperty('--cprp-toolbar-offset-y');
      wrap.style.minHeight = '';
      wasFixed = false;
      syncedFilterTops.delete(section);
    };

    const update = () => {
      ticking = false;

      if (!isStickyEnabled() || !isToolbarReady()) {
        clearFixed();
        return;
      }

      checkPageTopAnchorRefresh();

      const filterTop = getMobileFilterTop();

      if (wasFixed) {
        if (
          toolbar.classList.contains('is-entering') ||
          toolbar.classList.contains('is-header-reveal') ||
          isHeaderRevealing()
        ) {
          return;
        }

        if (shouldReleaseFixedToolbar(sentinel, filterTop)) {
          clearFixed();
        }
        return;
      }

      if (isHeaderRevealing()) return;

      if (toolbar.getBoundingClientRect().bottom <= filterTop - STICKY_HYSTERESIS) {
        const rect = toolbar.getBoundingClientRect();
        wrap.style.minHeight = `${rect.height}px`;
        syncMobileFilterTop(section, undefined, true);
        toolbar.classList.add('is-fixed');
        wasFixed = true;
        playFixedEnter(toolbar, rect.top - filterTop);
      }
    };

    const scheduleUpdate = () => {
      if (!ticking) {
        ticking = true;
        requestAnimationFrame(update);
      }
    };

    const onAnchorChange = (detail = {}) => {
      if (
        detail.animateFrom != null &&
        detail.animateTo != null &&
        toolbar.classList.contains('is-fixed')
      ) {
        animateHeaderReveal(section, toolbar, detail.animateFrom, detail.animateTo);
        return;
      }

      syncMobileFilterTop(section, undefined, Boolean(detail.snap));
    };

    const unsubscribeAnchor = subscribeMobileHeaderAnchor(onAnchorChange);

    const start = () => {
      syncMobileFilterTop(section, undefined, true);
      scheduleUpdate();
    };

    let contentObserver = null;
    if (isToolbarReady()) {
      start();
    } else {
      contentObserver = new MutationObserver(() => {
        if (!isToolbarReady()) return;
        contentObserver.disconnect();
        contentObserver = null;
        start();
      });
      contentObserver.observe(toolbar, { childList: true, subtree: true });
    }

    window.addEventListener('scroll', scheduleUpdate, { passive: true });
    window.addEventListener('resize', scheduleUpdate, { passive: true });

    registerSectionCleanup(section, () => {
      window.removeEventListener('scroll', scheduleUpdate);
      window.removeEventListener('resize', scheduleUpdate);
      contentObserver?.disconnect();
      unsubscribeAnchor();
      if (toolbar.isConnected) {
        toolbar.classList.remove('is-fixed', 'is-entering', 'is-header-reveal');
        toolbar.style.removeProperty('--cprp-toolbar-offset-y');
      }
      if (wrap.isConnected) {
        wrap.style.minHeight = '';
      }
    });
  }

  function initPrototypeMobileFilterSticky(section) {
    const anchor = section.querySelector('[data-cprp-mobile-filter-anchor]');
    const sentinel = section.querySelector('[data-cprp-mobile-filter-sentinel]');
    const fixed = section.querySelector('[data-cprp-mobile-filter-fixed]');
    const toggleBtn = anchor?.querySelector('.cprp-mobile-toggle-btn');
    if (!anchor || !sentinel || !fixed || !toggleBtn) return;

    ensureMobileHeaderAnchor();

    let wasFixedVisible = false;
    let observer = null;
    let ticking = false;

    const isStickyEnabled = () =>
      window.innerWidth <= MOBILE_MAX && !section.classList.contains('cprp-mobile-open');

    const setFixedVisible = (visible) => {
      if (visible === wasFixedVisible) return;

      anchor.classList.toggle('is-offscreen', visible);

      if (visible) {
        fixed.removeAttribute('hidden');
        syncMobileFilterTop(section);
        fixed.classList.add('is-visible');
      } else {
        fixed.classList.remove('is-visible', 'is-snapping', 'is-header-reveal');
        fixed.style.removeProperty('--cprp-toolbar-offset-y');
        fixed.setAttribute('hidden', '');
      }
      wasFixedVisible = visible;
    };

    const update = () => {
      ticking = false;

      if (!isStickyEnabled()) {
        setFixedVisible(false);
        return;
      }

      checkPageTopAnchorRefresh();

      if (isNearPageTop()) {
        setFixedVisible(false);
        return;
      }

      if (wasFixedVisible && isHeaderRevealing()) return;

      const filterTop = getMobileFilterTop();
      const anchorRect = toggleBtn.getBoundingClientRect();
      const shouldShow = wasFixedVisible
        ? anchorRect.bottom <= filterTop + STICKY_HYSTERESIS
        : anchorRect.bottom <= filterTop - STICKY_HYSTERESIS;

      setFixedVisible(shouldShow);
    };

    const scheduleUpdate = () => {
      if (!ticking) {
        ticking = true;
        requestAnimationFrame(update);
      }
    };

    const onAnchorChange = (detail = {}) => {
      if (
        detail.animateFrom != null &&
        detail.animateTo != null &&
        wasFixedVisible
      ) {
        animateHeaderReveal(section, fixed, detail.animateFrom, detail.animateTo);
      } else {
        syncMobileFilterTop(section, undefined, Boolean(detail.snap));
      }

      bindObserver();
      scheduleUpdate();
    };

    const unsubscribeAnchor = subscribeMobileHeaderAnchor(onAnchorChange);

    const bindObserver = () => {
      if (observer) {
        observer.disconnect();
        observer = null;
      }

      if (!isStickyEnabled()) {
        setFixedVisible(false);
        return;
      }

      observer = new IntersectionObserver(
        () => scheduleUpdate(),
        {
          threshold: 0,
          rootMargin: `-${getMobileFilterTop()}px 0px 0px 0px`,
        }
      );
      observer.observe(sentinel);
    };

    syncMobileFilterTop(section, undefined, true);
    bindObserver();
    scheduleUpdate();

    const onResize = () => {
      bindObserver();
      scheduleUpdate();
    };

    window.addEventListener('scroll', scheduleUpdate, { passive: true });
    window.addEventListener('resize', onResize, { passive: true });

    registerSectionCleanup(section, () => {
      window.removeEventListener('scroll', scheduleUpdate);
      window.removeEventListener('resize', onResize);
      observer?.disconnect();
      unsubscribeAnchor();
      if (anchor.isConnected) {
        anchor.classList.remove('is-offscreen');
      }
      if (fixed.isConnected) {
        fixed.classList.remove('is-visible', 'is-snapping', 'is-header-reveal');
        fixed.style.removeProperty('--cprp-toolbar-offset-y');
        fixed.setAttribute('hidden', '');
      }
    });
  }

  function initFilterToolbarSticky(section) {
    if (section.querySelector('#athos-toolbar')) {
      initAthosToolbarSticky(section);
      return;
    }
    initPrototypeMobileFilterSticky(section);
  }

  function getFilterToolbarRow(section) {
    return (
      section.querySelector('#athos-toolbar .ss__theme.ss__toolbar') ||
      section.querySelector('.cprp-toolbar.cprp-toolbar--mobile') ||
      section.querySelector('.cprp-toolbar:not(.cprp-toolbar--desktop)')
    );
  }

  function updateFilterButtonCenter(section) {
    if (window.innerWidth > MOBILE_MAX) {
      section.style.removeProperty('--cprp-filter-btn-half-width');
      section.style.removeProperty('--cprp-filter-btn-margin-left');
      return;
    }

    const row = getFilterToolbarRow(section);
    if (!row) return;

    const width = Math.round(row.getBoundingClientRect().width);
    if (width <= 0) return;

    const halfWidth = Math.round(width / 2);
    section.style.setProperty('--cprp-filter-btn-half-width', `${halfWidth}px`);
    section.style.setProperty('--cprp-filter-btn-margin-left', `calc(50vw - ${halfWidth}px)`);
  }

  function initFilterButtonCenter(section) {
    let pending = false;

    const schedule = () => {
      if (pending) return;
      pending = true;
      requestAnimationFrame(() => {
        pending = false;
        updateFilterButtonCenter(section);
      });
    };

    schedule();

    window.addEventListener('resize', schedule, { passive: true });

    let mutationObserver = null;
    const athosToolbar = section.querySelector('#athos-toolbar');
    if (athosToolbar) {
      mutationObserver = new MutationObserver(schedule);
      mutationObserver.observe(athosToolbar, { childList: true, subtree: true, characterData: true });
    }

    registerSectionCleanup(section, () => {
      window.removeEventListener('resize', schedule);
      mutationObserver?.disconnect();
    });
  }

  function initMobileNavOverlayState(section) {
    const nav = document.getElementById('mobile-nav-main');
    const overlay = document.getElementById('mobile-nav-overlay');
    if (!nav && !overlay) return;

    const sync = () => {
      document.body.classList.toggle(
        'cprp-mobile-nav-open',
        Boolean(nav?.classList.contains('active') || overlay?.classList.contains('active'))
      );
    };

    const observer = new MutationObserver(sync);
    if (nav) observer.observe(nav, { attributes: true, attributeFilter: ['class'] });
    if (overlay) observer.observe(overlay, { attributes: true, attributeFilter: ['class'] });

    sync();

    registerSectionCleanup(section, () => {
      observer.disconnect();
      document.body.classList.remove('cprp-mobile-nav-open');
    });
  }

  function init(section) {
    if (section.dataset.cprpInit === '1') return;
    section.dataset.cprpInit = '1';

    initFacetToggleAccessibility(section);

    const toggle = section.querySelector('[data-cprp-toggle]');
    if (toggle) {
      const label = toggle.querySelector('.cprp-toggle-btn__label');
      const updateLabel = () => {
        const isHidden = section.classList.contains('cprp-filters-hidden');
        if (label) {
          label.textContent = isHidden ? 'Show Filters' : 'Hide Filters';
        }
        toggle.setAttribute('aria-pressed', isHidden ? 'false' : 'true');
      };

      updateLabel();

      toggle.addEventListener('click', () => {
        section.classList.toggle('cprp-filters-hidden');
        updateLabel();
        window.dispatchEvent(new Event('scroll'));
      });
    }

    const setGroupHeight = (group, isOpen) => {
      const toggleBtn = group.querySelector('.cprp-filter-group__toggle');
      const content = group.querySelector('.cprp-filter-group__items');
      if (!toggleBtn || !content) return;

      if (window.innerWidth >= 1024) {
        if (isOpen) {
          content.hidden = false;
          content.style.height = 'auto';
          content.style.overflow = '';
        } else {
          content.style.height = '0px';
          content.style.overflow = 'hidden';
          content.hidden = true;
        }
        return;
      }

      if (isOpen) {
        content.hidden = false;
        content.style.height = `${content.scrollHeight}px`;
      } else {
        content.style.height = '0px';
        content.hidden = true;
      }
    };

    section.querySelectorAll('.cprp-filter-group').forEach((group) => {
      const toggleBtn = group.querySelector('.cprp-filter-group__toggle');
      if (!toggleBtn) return;
      const isOpen = group.classList.contains('cprp-filter-group--open') || toggleBtn.getAttribute('aria-expanded') === 'true';
      setGroupHeight(group, isOpen);

      toggleBtn.addEventListener('click', () => {
        const nextOpen = !group.classList.contains('cprp-filter-group--open');
        group.classList.toggle('cprp-filter-group--open', nextOpen);
        toggleBtn.setAttribute('aria-expanded', nextOpen ? 'true' : 'false');
        setFacetToggleLabel(toggleBtn);
        setGroupHeight(group, nextOpen);
        if (window.innerWidth >= 1024) {
          const stickyEl = section.querySelector('.cprp-filters__sticky');
          const outer = stickyEl?.querySelector('.cprp-filters__scroll-outer');
          if (stickyEl) updateFilterScrollMax(section, stickyEl);
          requestAnimationFrame(() => {
            if (outer) updateFilterScrollOverflow(outer);
          });
        }
        window.dispatchEvent(new Event('scroll'));
      });
    });

    const headerToggle = section.querySelector('[data-cprp-header-toggle]');
    const headerContent = section.querySelector('[data-cprp-header-content]');
    if (headerToggle && headerContent) {
      headerToggle.addEventListener('click', () => {
        const header = headerToggle.closest('.cprp-filters__header');
        const isOpen = header?.classList.toggle('cprp-filters__header--open');
        headerToggle.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
        headerContent.toggleAttribute('hidden', !isOpen);
        window.dispatchEvent(new Event('scroll'));
      });
    }

    const sortToggle = section.querySelector('[data-cprp-sort-toggle]');
    const sortMenu = section.querySelector('[data-cprp-sort-menu]');
    if (sortToggle && sortMenu) {
      sortToggle.addEventListener('click', () => {
        const isOpen = sortToggle.classList.toggle('cprp-sort--open');
        sortToggle.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
        sortMenu.toggleAttribute('hidden', !isOpen);
      });
    }

    const refreshSwatches = () => {
      section.querySelectorAll('[data-cprp-swatches]').forEach((container) => {
        const swatches = Array.from(container.querySelectorAll('[data-cprp-swatch]'));
        const more = container.querySelector('[data-cprp-swatch-more]');
        const total = parseInt(container.dataset.count || swatches.length, 10);
        if (!swatches.length || !more) return;

        swatches.forEach((swatch) => {
          swatch.style.display = 'inline-flex';
        });
        more.style.display = 'inline-flex';

        const containerWidth = container.clientWidth;
        if (!containerWidth) return;

        const swatchRect = swatches[0].getBoundingClientRect();
        const moreRect = more.getBoundingClientRect();
        const gap = parseFloat(getComputedStyle(container).gap || '0');
        const swatchWidth = swatchRect.width;
        const moreWidth = moreRect.width;

        const maxVisible = Math.max(0, Math.floor((containerWidth - moreWidth) / (swatchWidth + gap)));
        const visibleCount = Math.min(swatches.length, maxVisible);
        const hiddenCount = Math.max(0, total - visibleCount);

        swatches.forEach((swatch, index) => {
          swatch.style.display = index < visibleCount ? 'inline-flex' : 'none';
        });

        if (hiddenCount > 0) {
          more.textContent = `+${hiddenCount}`;
          more.style.display = 'inline-flex';
        } else {
          more.style.display = 'none';
        }
      });
    };

    const refreshTitleHeights = () => {
      const cards = Array.from(section.querySelectorAll('.cprp-card'));
      if (!cards.length) return;

      const titles = cards.map((card) => card.querySelector('.cprp-card__title'));
      titles.forEach((title) => {
        if (title) title.style.minHeight = 'auto';
      });

      const rows = new Map();
      cards.forEach((card) => {
        const title = card.querySelector('.cprp-card__title');
        if (!title) return;
        const top = card.offsetTop;
        if (!rows.has(top)) rows.set(top, []);
        rows.get(top).push(title);
      });

      rows.forEach((rowTitles) => {
        let max = 0;
        rowTitles.forEach((title) => {
          max = Math.max(max, title.offsetHeight);
        });
        rowTitles.forEach((title) => {
          title.style.minHeight = `${max}px`;
        });
      });
    };

    refreshSwatches();
    refreshTitleHeights();

    const onLayoutResize = () => {
      refreshSwatches();
      refreshTitleHeights();
    };
    window.addEventListener('resize', onLayoutResize, { passive: true });
    registerSectionCleanup(section, () => {
      window.removeEventListener('resize', onLayoutResize);
    });

    const mobileToggles = Array.from(section.querySelectorAll('[data-cprp-mobile-toggle]'));
    const mobilePanel = section.querySelector('.cprp-mobile-panel');
    const mobileClose = section.querySelector('[data-cprp-mobile-close]');

    if (mobileToggles.length && mobilePanel) {
      const mobileLabels = mobileToggles.map((btn) => btn.querySelector('.cprp-toggle-btn__label'));

      const setMobileState = (open) => {
        mobilePanel.hidden = !open;
        mobileToggles.forEach((btn) => {
          btn.setAttribute('aria-expanded', open ? 'true' : 'false');
        });
        mobileLabels.forEach((label) => {
          if (label) label.textContent = open ? 'Hide Filters' : 'Filter & Sort';
        });
        section.classList.toggle('cprp-mobile-open', open);
        window.dispatchEvent(new Event('scroll'));
      };

      const scrollMobilePanelIntoView = () => {
        const toolbar = mobilePanel.closest('.cprp-toolbar');
        if (!toolbar) return;

        const top = window.scrollY + toolbar.getBoundingClientRect().top - getMobileFilterTop();
        window.scrollTo({ top: Math.max(0, top), behavior: 'auto' });
      };

      setMobileState(false);

      mobileToggles.forEach((btn) => {
        btn.addEventListener('click', () => {
          const shouldOpen = mobilePanel.hidden;
          const openedFromFixedButton = shouldOpen && Boolean(btn.closest('[data-cprp-mobile-filter-fixed]'));

          setMobileState(shouldOpen);

          if (openedFromFixedButton) {
            requestAnimationFrame(scrollMobilePanelIntoView);
          }
        });
      });

      if (mobileClose) {
        mobileClose.addEventListener('click', () => setMobileState(false));
      }
    }

    initDesktopFilterSticky(section);
    initFilterToolbarSticky(section);
    initFilterButtonCenter(section);
    initMobileNavOverlayState(section);
  }

  document.addEventListener('DOMContentLoaded', () => {
    document.querySelectorAll('.collection-page-redesign-prototype').forEach(init);
  });

  document.addEventListener('shopify:section:load', (event) => {
    const section = getSectionFromEvent(event);
    if (section) {
      destroySection(section);
      init(section);
    }
  });

  document.addEventListener('shopify:section:unload', (event) => {
    const section = getSectionFromEvent(event);
    if (section) {
      destroySection(section);
    }
  });
})();